import { BehaviorSubject, finalize, map, Observable, shareReplay, tap } from 'rxjs';
import { inject, Injectable } from '@angular/core';
import { environment } from '@env';
import { HttpClient, HttpContext } from '@angular/common/http';
import { OperationCreateInput, OperationListRes, OperationProfile } from '../types/basic-operation.type';
import { buildHttpParams } from '@app/shared/utils';
import { OperationType } from '@app/types/operation.type';
import { MessageService } from '@app/shared/services/message.service';
import { SKIP_ERROR_TOKEN } from '@app/shared/interceptors/auth.interceptor';

@Injectable()
export class BasicOperationApiService {
	readonly #httpClient = inject(HttpClient);
	readonly #notify = inject(MessageService);

	readonly #loadingSub$ = new BehaviorSubject(false);
	readonly #loadingProfileSub$ = new BehaviorSubject(false);

	loadingList$ = this.#loadingSub$.asObservable().pipe(
		shareReplay(1)
	)
	loadingProfile$ = this.#loadingProfileSub$.asObservable().pipe(
		shareReplay(1)
	)

	fetchOperations(shopId: number, type: OperationType): Observable<OperationListRes	> {
		const params = buildHttpParams({ status: 'DRAFT', type, shops: shopId, limit: 1000 });

		this.#loadingSub$.next(true);
		return this.#httpClient.get<OperationListRes	>(`${environment.apiUrl}/shops/operations`, { params }).pipe(
			finalize(() => this.#loadingSub$.next(false))
		);
	}

	createOperation(operation: Partial<OperationCreateInput>): Observable<number> {
		this.#loadingProfileSub$.next(true);
		return this.#httpClient.post<number>(`${environment.apiUrl}/shops/operation/${operation.type}`, operation).pipe(
			finalize(() => this.#loadingProfileSub$.next(false))
		);
	}

	updateOperationComment({ id_operation, type, comment }: { id_operation: number, type: OperationType, comment: string | null }): Observable<number> {
		this.#loadingProfileSub$.next(true);
		return this.#httpClient.patch<number>(`${environment.apiUrl}/shops/operation/${type}/${id_operation}`, { comment }).pipe(
			finalize(() => this.#loadingProfileSub$.next(false))
		);
	}

	validateOperation({ id_operation, type, force }: { id_operation: number, type: OperationType, force: boolean }): Observable<number> {
		this.#loadingProfileSub$.next(true);

		const params: Record<string, boolean> = force ? { force: true } : { };
		let context = new HttpContext();
		if (!force) {
			context = context.set(SKIP_ERROR_TOKEN, true)
		}

		return this.#httpClient.post<number>(`${environment.apiUrl}/shops/operation/${type}/${id_operation}/commit`, { }, { context, params }).pipe(
			finalize(() => this.#loadingProfileSub$.next(false))
		);
	}

	deleteOperation({ id_operation, type }: { id_operation: number, type: OperationType }): Observable<number> {
		this.#loadingProfileSub$.next(true);
		return this.#httpClient.delete<number>(`${environment.apiUrl}/shops/operation/${type}/${id_operation}`).pipe(
			finalize(() => this.#loadingProfileSub$.next(false))
		);
	}

	fetchOperationProfile({ id_operation, type }: { id_operation: number, type: OperationType }): Observable<OperationProfile> {
		this.#loadingProfileSub$.next(true);
		return this.#httpClient.get<OperationProfile>(`${environment.apiUrl}/shops/operation/${type}/${id_operation}`).pipe(
			finalize(() => this.#loadingProfileSub$.next(false))
		);
	}

	addOperationProduct(
		{ id_operation, type, input }:
		{
			id_operation: number,
			type: OperationType,
			input: { id_product: number, quantity: number }
		}
	): Observable<{ success: boolean, warnings: string[] }> {
		this.#loadingProfileSub$.next(true);
		return this.#httpClient.post<{ success: boolean, warnings: string[] }>(`${environment.apiUrl}/shops/operation/${type}/${id_operation}/product`, input).pipe(
			tap(({ warnings }) => {
				if (warnings.length > 0) {
					this.#notify.show(warnings.join('<br>'), 'alert-warning');
				}
			}),
			finalize(() => this.#loadingProfileSub$.next(false))
		);
	}

	updateOperationProduct(
		{ id_operation, type, id_reference, input }:
		{
			id_operation: number,
			type: OperationType,
			id_reference: number,
			input: { id_product: number, quantity: number }
		}
	): Observable<{ success: boolean, warnings: string[] }> {
		this.#loadingProfileSub$.next(true);
		return this.#httpClient.patch<{ success: boolean, warnings: string[] }>(`${environment.apiUrl}/shops/operation/${type}/${id_operation}/product/${id_reference}`, input).pipe(
			tap(({ warnings }) => {
				if (warnings.length > 0) {
					this.#notify.show(warnings.join('<br>'), 'alert-warning');
				}
			}),
			finalize(() => this.#loadingProfileSub$.next(false))
		);
	}

	deleteOperationProduct(
		{ id_operation, type, id_reference }:
		{ id_operation: number, type: OperationType, id_reference: number }
	): Observable<number> {
		this.#loadingProfileSub$.next(true);
		return this.#httpClient.delete<number>(`${environment.apiUrl}/shops/operation/${type}/${id_operation}/product/${id_reference}`).pipe(
			finalize(() => this.#loadingProfileSub$.next(false))
		);
	}
}
